import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, CheckCircle2, AlertCircle, Phone, Mail, Info } from "lucide-react";
import axios from "axios";
import { BROKER, IMAGES, PRODUCTS, FACE_AMOUNTS, INQUIRING_FOR, US_STATES } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  age: "",
  product_interest: "",
  face_amount: "",
  inquiring_for: "",
  state: "",
  beneficiary: "",
  message: "",
};

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      setStatus({ state: "error", msg: "Name, email and phone are required." });
      return;
    }
    setStatus({ state: "submitting", msg: "" });
    try {
      await axios.post(`${API}/leads`, form);
      setStatus({
        state: "success",
        msg: "Thank you. I'll personally reach out within 24 hours.",
      });
      setForm(initial);
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please call directly.";
      setStatus({ state: "error", msg: String(detail) });
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-gradient-to-b from-cream/40 via-ivory to-cream/60 py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 grid-overlay opacity-25" />
      <div className="absolute top-20 right-0 w-96 h-96 bg-gold/8 rounded-full blur-3xl" />
      <div className="absolute bottom-10 left-0 w-80 h-80 bg-gold-light/8 rounded-full blur-3xl" />

      {/* Subtle image at far edge — smaller, lower */}
      <div className="absolute right-0 bottom-0 w-1/3 h-2/3 opacity-15 hidden lg:block pointer-events-none">
        <img src={IMAGES.contact} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-ivory" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: copy + contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-px bg-gold" />
              <span className="eyebrow text-gold">Get a Free Quote</span>
            </div>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink">
              Let's protect what you've{" "}
              <span className="italic gold-text-gradient">built.</span>
            </h2>
            <p className="mt-8 text-ink/65 text-base sm:text-lg leading-relaxed font-light max-w-md">
              Share a few details below and I'll personally follow up within 24 hours with a tailored,
              no-obligation quote across all 12 carriers I represent.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={BROKER.phoneLink}
                data-testid="contact-phone"
                className="flex items-center gap-4 text-ink/80 hover:text-gold transition-colors"
              >
                <Phone className="w-5 h-5 text-gold shrink-0" />
                <span className="text-lg font-light">{BROKER.phone}</span>
              </a>
              <a
                href={BROKER.emailLink}
                data-testid="contact-email"
                className="flex items-center gap-4 text-ink/80 hover:text-gold transition-colors"
              >
                <Mail className="w-5 h-5 text-gold shrink-0" />
                <span className="text-lg font-light break-all">{BROKER.email}</span>
              </a>
              <div className="pt-4 eyebrow text-ink/50 text-[10px]">
                NPN {BROKER.npn} · Licensed Broker
              </div>
            </div>

            {/* Disclaimer card */}
            <div className="mt-10 glass p-5 border-l-2 border-gold">
              <div className="flex gap-3">
                <Info className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                <div>
                  <div className="eyebrow text-ink/60 text-[10px] mb-1.5">Important</div>
                  <p className="text-xs sm:text-sm text-ink/70 leading-relaxed font-light">
                    Rates are based on your <span className="text-ink font-medium">age</span> and{" "}
                    <span className="text-ink font-medium">health</span>. Life insurance is offered to{" "}
                    <span className="text-gold font-medium">ages 0 through 89</span>. Your quote is
                    100% free and obligation-free.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.form
            onSubmit={submit}
            noValidate
            data-testid="contact-form"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="lg:col-span-7 relative glass p-8 sm:p-10 lg:p-12"
          >
            {/* corner accents */}
            <span className="absolute top-0 left-0 w-12 h-px bg-gold" />
            <span className="absolute top-0 left-0 w-px h-12 bg-gold" />
            <span className="absolute bottom-0 right-0 w-12 h-px bg-gold" />
            <span className="absolute bottom-0 right-0 w-px h-12 bg-gold" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
              <Field label="Full Name" required id="lead-name" value={form.name} onChange={update("name")} placeholder="Jane Doe" />
              <Field label="Phone" required id="lead-phone" type="tel" value={form.phone} onChange={update("phone")} placeholder="(555) 555-5555" />
              <Field label="Email" required id="lead-email" type="email" value={form.email} onChange={update("email")} placeholder="jane@email.com" />
              <Field label="Age" id="lead-age" type="number" min="0" max="89" value={form.age} onChange={update("age")} placeholder="0 – 89" />

              <Select
                id="lead-product"
                label="Type of Insurance"
                value={form.product_interest}
                onChange={update("product_interest")}
                options={[
                  { value: "", label: "Not sure — help me decide" },
                  ...PRODUCTS.map((p) => ({ value: p.name, label: p.name })),
                  { value: "Whole Life for Kids", label: "Whole Life for Kids (0–17)" },
                  { value: "IUL for Kids", label: "IUL for Kids (0–17)" },
                ]}
              />
              <Select
                id="lead-face-amount"
                label="Requested Face Amount"
                value={form.face_amount}
                onChange={update("face_amount")}
                options={FACE_AMOUNTS.map((f) => ({ value: f === "Not sure yet" ? "" : f, label: f }))}
              />
              <Select
                id="lead-inquiring-for"
                label="Who Are You Inquiring For?"
                value={form.inquiring_for}
                onChange={update("inquiring_for")}
                options={[{ value: "", label: "Select..." }, ...INQUIRING_FOR.map((i) => ({ value: i, label: i }))]}
              />
              <Select
                id="lead-state"
                label="State of Residence"
                value={form.state}
                onChange={update("state")}
                options={[{ value: "", label: "Select state..." }, ...US_STATES.map((s) => ({ value: s, label: s }))]}
              />

              <div className="sm:col-span-2">
                <Field
                  label="Beneficiary (Full Name)"
                  id="lead-beneficiary"
                  value={form.beneficiary}
                  onChange={update("beneficiary")}
                  placeholder="The person you'd like to protect"
                />
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="lead-message" className="eyebrow text-ink/55 text-[10px] mb-3 block">
                  Additional Notes (optional)
                </label>
                <textarea
                  id="lead-message"
                  data-testid="lead-message"
                  rows={3}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Anything else you'd like me to know..."
                  className="w-full bg-transparent border-b border-line focus:border-gold text-ink py-3 outline-none transition-colors resize-none placeholder:text-ink/30 font-light"
                />
              </div>
            </div>

            {status.state === "error" && (
              <div data-testid="form-error" className="mt-6 flex items-start gap-3 text-red-700 text-sm">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{status.msg}</span>
              </div>
            )}
            {status.state === "success" && (
              <div data-testid="form-success" className="mt-6 flex items-start gap-3 text-gold text-sm">
                <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{status.msg}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={status.state === "submitting"}
              data-testid="contact-submit-button"
              className="mt-10 group relative inline-flex items-center justify-center gap-3 bg-ink text-gold-light px-10 py-4 text-sm font-semibold tracking-[0.2em] uppercase overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-500"
            >
              <span className="absolute inset-0 bg-gold scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
              {status.state === "submitting" ? (
                <>
                  <Loader2 className="relative w-4 h-4 animate-spin" />
                  <span className="relative">Sending</span>
                </>
              ) : (
                <>
                  <span className="relative group-hover:text-ink transition-colors">Send My Request</span>
                  <ArrowRight className="relative w-4 h-4 group-hover:translate-x-1 group-hover:text-ink transition-all" />
                </>
              )}
            </button>

            <p className="mt-6 text-xs text-ink/40 font-light leading-relaxed">
              Your information is kept strictly confidential and is used solely to prepare your
              personalized quote. By submitting, you consent to be contacted by Zaylan Welton
              regarding your inquiry.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, id, required, ...rest }) => (
  <div>
    <label htmlFor={id} className="eyebrow text-ink/55 text-[10px] mb-3 block">
      {label}
      {required && <span className="text-gold ml-1">*</span>}
    </label>
    <input
      id={id}
      data-testid={id}
      required={required}
      {...rest}
      className="w-full bg-transparent border-b border-line focus:border-gold text-ink py-3 outline-none transition-colors placeholder:text-ink/30 font-light"
    />
  </div>
);

const Select = ({ id, label, value, onChange, options, required }) => (
  <div>
    <label htmlFor={id} className="eyebrow text-ink/55 text-[10px] mb-3 block">
      {label}
      {required && <span className="text-gold ml-1">*</span>}
    </label>
    <div className="relative">
      <select
        id={id}
        data-testid={id}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border-b border-line focus:border-gold text-ink py-3 pr-8 outline-none transition-colors appearance-none cursor-pointer font-light"
      >
        {options.map((opt) => (
          <option key={opt.value + opt.label} value={opt.value} className="bg-ivory text-ink">
            {opt.label}
          </option>
        ))}
      </select>
      <svg className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-gold" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  </div>
);

export default Contact;
